import { useState, useEffect } from 'react';
import { Outlet, Link, NavLink, useLocation } from 'react-router-dom';
import './landing.css';

/**
 * Editorial-scientific chrome for the marketing site.
 *
 * Aesthetic direction: "field journal crossed with instrument panel."
 * Cream paper stock (#F5F1E8), ink-navy contrast sections (#0B0D12),
 * blue-600 as the measurement accent, archivist-yellow as highlight.
 * Fraunces (variable serif, opsz + wonk axes) for display, Instrument
 * Sans for body, JetBrains Mono for coordinates / figure numbers.
 *
 * Header is a hairline rule with meta text on either side — it reads
 * more like a magazine masthead than a typical SaaS nav. Grain overlay
 * is fixed + pointer-events:none across the whole surface.
 */
export default function LandingLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location]);

  const navLinks = [
    { to: '/features', label: 'Features', num: '01' },
    { to: '/pricing',  label: 'Pricing',  num: '02' },
    { to: '/about',    label: 'About',    num: '03' },
    { to: '/contact',  label: 'Contact',  num: '04' },
  ];

  return (
    <div className="cz-root">
      {/* Grain overlay — subtle paper texture across the whole site */}
      <div className="cz-grain" aria-hidden="true" />

      {/* Masthead */}
      <header className="cz-masthead">
        <div className="cz-masthead-ticker">
          <span>Field Journal · Vol. 01 · Issue 04</span>
          <span className="dot">·</span>
          <span>Apr 2026</span>
          <span className="dot">·</span>
          <span>N 37.7749 / W 122.4194</span>
        </div>

        <nav className="cz-nav">
          <Link to="/" className="cz-wordmark">
            <span className="cz-wordmark-c">C</span>alibZa
            <sup className="cz-wordmark-tm">®</sup>
          </Link>

          <ul className={`cz-nav-links ${menuOpen ? 'open' : ''}`}>
            {navLinks.map(({ to, label, num }) => (
              <li key={to}>
                <NavLink to={to} className={({ isActive }) => `cz-nav-link ${isActive ? 'is-active' : ''}`}>
                  <span className="cz-nav-num">{num}</span>
                  <span className="cz-nav-label">{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="cz-nav-cta">
            <Link to="/login" className="cz-link-ghost">Sign in</Link>
            <Link to="/register" className="cz-btn cz-btn-solid">
              Start free pilot
              <span className="cz-btn-arrow">→</span>
            </Link>
          </div>

          <button className="cz-hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </nav>
      </header>

      {/* Page content */}
      <main className="cz-main">
        <Outlet />
      </main>

      {/* Colophon / Footer */}
      <footer className="cz-footer">
        <div className="cz-footer-rule">
          <span className="cz-footer-rule-label">End of field notes</span>
        </div>

        <div className="cz-footer-inner">
          <div className="cz-footer-brand">
            <Link to="/" className="cz-wordmark cz-wordmark-lg">
              <span className="cz-wordmark-c">C</span>alibZa
            </Link>
            <p className="cz-footer-tag">
              Calibration intelligence for modern organisations. Replace gut-feel
              ratings with a measurement discipline.
            </p>
            <div className="cz-footer-coords">
              <span className="cz-mono-label">Headquartered</span>
              <span>San Francisco, CA</span>
              <span className="cz-mono-label">Jurisdictions</span>
              <span>US · CA · UK · EU · IN · AU</span>
            </div>
          </div>

          <div className="cz-footer-cols">
            {[
              { heading: 'Instrument',  num: 'I',   links: [
                ['Features','/features'],['Pricing','/pricing'],['Changelog','#'],['Roadmap','#'],['Status','#'],
              ]},
              { heading: 'Company',     num: 'II',  links: [
                ['About','/about'],['Journal','#'],['Careers','#'],['Press','#'],['Contact','/contact'],
              ]},
              { heading: 'Resources',   num: 'III', links: [
                ['Documentation','#'],['API Reference','#'],['Integrations','#'],['Security','#'],['GDPR','#'],
              ]},
              { heading: 'Legal',       num: 'IV',  links: [
                ['Privacy','/privacy'],['Terms','/terms'],['Cookies','/privacy#cookies'],['DPA','/terms#customer-data'],['SLA','/terms#security-sla'],
              ]},
            ].map(({ heading, num, links }) => (
              <div key={heading} className="cz-footer-col">
                <p className="cz-footer-col-heading">
                  <span className="cz-footer-col-num">{num}</span>
                  {heading}
                </p>
                <ul>
                  {links.map(([l, to]) => (
                    <li key={l}>
                      {to.startsWith('/') ? (
                        <Link to={to} className="cz-footer-link">{l}</Link>
                      ) : (
                        <a href={to} className="cz-footer-link">{l}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="cz-footer-bottom">
          <p>© {new Date().getFullYear()} CalibZa, Inc. · All measurements reserved.</p>
          <p className="cz-mono-label">Set in Fraunces &amp; Instrument Sans · Printed in the browser</p>
        </div>
      </footer>
    </div>
  );
}
